import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUserSession } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`SSO error: ${errorParam}`);
      toast.error('SSO login failed. Please try again.');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    if (!code) {
      setError('No authorization code received from SSO.');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    // Validate state to prevent CSRF
    const savedState = sessionStorage.getItem('oauth_state');
    if (state && savedState && state !== savedState) {
      setError('Invalid state parameter. Possible CSRF attack.');
      toast.error('Authentication failed. Please try again.');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    try {
      // Exchange code for SSO token via backend
      const response = await api.post('/auth/sso/callback', {
        code,
        redirect_uri: `${window.location.origin}/auth/callback`
      });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('token', token);

        if (setUserSession) {
          setUserSession(token, user);
        }

        toast.success('Logged in with CIXIO SSO!');

        const redirectTo = sessionStorage.getItem('oauth_redirect_from') || '/';
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_redirect_from');

        // Redirect based on user type
        if (user?.userType === 'support' || user?.userType === 'admin') {
          navigate('/support-dashboard');
        } else {
          navigate(redirectTo);
        }
      } else {
        throw new Error(response.data.message || 'SSO login failed');
      }
    } catch (err) {
      console.error('SSO callback error:', err);
      setError(err.response?.data?.message || err.message || 'SSO login failed');
      toast.error('SSO login failed. Please try again.');
      setTimeout(() => navigate('/login'), 3000);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      background: '#f8fafc',
    }}>
      {error ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
          <h2 style={{ color: '#ef4444' }}>Authentication Failed</h2>
          <p style={{ color: '#6b7280' }}>{error}</p>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Redirecting to login...</p>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <h2 style={{ color: '#1f2937' }}>Completing Login</h2>
          <p style={{ color: '#6b7280' }}>Please wait while we verify your SSO session...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
};

export default AuthCallback;
